import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, RotateCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function DataResetTab() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const fetchModules = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/resettable-modules");
      setModules(data.modules || []);
    } catch { toast.error("Failed to load module info"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const handleReset = async () => {
    if (!confirmDialog || confirmText !== "RESET") return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-module", {
        collection: confirmDialog.collection,
        reason: "Financial year reset",
      });
      toast.success(`${data.message} — ${data.deleted_count} entries removed`);
      setConfirmDialog(null);
      setConfirmText("");
      fetchModules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
    setResetting(false);
  };

  if (loading) return <p className="text-sm text-gray-400 p-4">Loading...</p>;

  return (
    <Card data-testid="data-reset-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <RotateCw size={18} className="text-red-600" /> FY Module Reset
        </CardTitle>
        <p className="text-sm text-gray-500">
          Hard-delete all entries in a module and reset serial numbers for the new financial year.
          This action is irreversible.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {modules.length === 0 && <p className="text-sm text-gray-400">No resettable modules configured.</p>}

        {modules.map((m) => (
          <div key={m.collection} className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50/50">
            <div>
              <p className="font-semibold text-sm">{m.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {m.current_fy} &middot; {m.active_entries} active / {m.total_entries} total entries &middot; Last serial: #{m.current_seq}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-600 hover:bg-red-50 gap-1.5"
              onClick={() => setConfirmDialog(m)}
              data-testid={`reset-btn-${m.collection}`}
            >
              <Trash2 size={14} /> Reset
            </Button>
          </div>
        ))}
      </CardContent>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => { setConfirmDialog(null); setConfirmText(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} /> Confirm Reset: {confirmDialog?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              This will <strong>permanently delete</strong> all <strong>{confirmDialog?.total_entries}</strong> entries
              from {confirmDialog?.label} and reset the serial counter to 0.
            </p>
            <p className="text-sm text-gray-600">
              New entries will start from <strong>JW-{modules[0]?.current_fy?.replace("FY 20", "").replace("-", "") || "2627"}-00001</strong>.
            </p>
            <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
            <div>
              <label className="text-xs text-gray-500">Type <strong>RESET</strong> to confirm:</label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="Type RESET"
                className="h-10 mt-1"
                data-testid="reset-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmDialog(null); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={confirmText !== "RESET" || resetting}
              onClick={handleReset}
              data-testid="reset-confirm-btn"
            >
              {resetting ? "Resetting..." : "Permanently Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
